import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { IS_NATIVE } from '../env';

/**
 * Keep people in the app. Field test 2026-09-05: links to the privacy policy,
 * terms, retailers etc. bounced users out to Safari, and "getting back can be
 * tricky mentally". On native every outbound http(s) link now opens in the
 * in-app browser sheet (Safari inside the app, Done button top-left);
 * affiliate tracking is unaffected because it IS Safari. mailto:/tel: and
 * in-app router links are left alone.
 */
@Injectable({ providedIn: 'root' })
export class ExternalLinksService {
  /** Install once (AppComponent). No-op on the web. */
  install(): void {
    if (!IS_NATIVE) return;
    document.addEventListener(
      'click',
      (ev) => {
        if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey) return;
        const a = (ev.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
        if (!a) return;
        const href = a.getAttribute('href') ?? '';
        if (!/^https?:\/\//i.test(href)) return; // router links, mailto:, tel:, anchors
        ev.preventDefault();
        void this.open(a.href);
      },
      true,
    );
  }

  /** Open a URL the in-app way on native, a new tab on the web. */
  async open(url: string): Promise<void> {
    // Our own legal/support pages open in "embed" mode: the site header and
    // its back-to-home link are hidden, so the sheet's Done is the only exit.
    if (/^https:\/\/(www\.)?vekkle\.com\/(privacy|terms|contact|guide)(\b|\/|$)/i.test(url) && !/[?&]embed=1/.test(url)) {
      url += (url.includes('?') ? '&' : '?') + 'embed=1';
    }
    if (!IS_NATIVE) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    try {
      await Browser.open({ url, presentationStyle: 'popover' });
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  }
}
